import * as React from 'react';
interface IUseDragHookSpec {
    constraintsTo: React.RefObject<HTMLElement>;
    target: React.RefObject<HTMLElement>;
    headDragX?: number;
    headDragY?: number;
    dragBufferX?: number;
    dragBufferY?: number;
    unit: 'px' | '%';
    onDragging?: (info: IConstrainedDragInfo) => void;
}

export interface IConstrainedDragInfo {
    top: number;
    left: number;
}

function validateMovement(clientX: number, clientY: number, box: ClientRect, spec: Partial<{ dragBufferX: number; dragBufferY: number; }>) {
    const { dragBufferX = 0, dragBufferY = 0 } = spec;
    return clientX >= (box.left - dragBufferX) &&
        clientY >= (box.top - dragBufferY) &&
        clientX <= (box.right + dragBufferX) &&
        clientY <= (box.bottom + dragBufferY);
}

export type SetDragPositionCallback = (x: number, y: number) => void;

export const useDrag = (spec: IUseDragHookSpec): [IConstrainedDragInfo, SetDragPositionCallback, boolean] => {
    const { constraintsTo: { current: currentElement } } = spec;
    const isDragging = React.useRef(false);

    const [currentDragPosition, setDragPosition] = React.useState<IConstrainedDragInfo>({
        left: spec.headDragX || 0,
        top: spec.headDragY || 0
    });

    const updateDragPosition = React.useCallback((event: MouseEvent | TouchEvent, dragging: boolean, options: Partial<{ dragBufferX: number; dragBufferY: number; }>, skipValidation?: boolean) => {
        if (!currentElement) {
            return;
        }
        const containerRect = currentElement!.getBoundingClientRect();

        let clientX = 0;
        let clientY = 0;
        if (event instanceof MouseEvent) {
            clientX = event.clientX;
            clientY = event.clientY;

            // When user click with right button the resize is stuck in resizing mode
            // until users clicks again, dont continue if right click is used.
            // HACK: MouseEvent does not have `which` from flow-bin v0.68.
            if (event.which === 3) {
                return;
            }
        } else if (event instanceof TouchEvent) {
            clientX = event.touches[0].clientX;
            clientY = event.touches[0].clientY;
        }

        if (!skipValidation && !validateMovement(clientX, clientY, containerRect, options)) {
            return;
        }
        isDragging.current = dragging;

        let left = Math.min(Math.max(clientX - containerRect.left, 0), containerRect.width);
        let top = Math.min(Math.max(clientY - containerRect.top, 0), containerRect.height);
        if (spec.unit === '%') {
            left = Math.max(0, Math.min(100, Math.round(left / containerRect.width * 100)));
            top = Math.max(0, Math.min(100, Math.round(top / containerRect.height * 100)));
        }
        const dragPosition = { left, top };
        setDragPosition(dragPosition);
        if (spec.onDragging === undefined) {
            return;
        }
        spec.onDragging(dragPosition);
    }, [currentElement, spec]);

    const setDragPositionCallback = React.useCallback((x: number, y: number) => {
        if (currentDragPosition.left === x && y === currentDragPosition.top) {
            return;
        }
        setDragPosition({ left: x, top: y });
    }, [currentDragPosition.left, currentDragPosition.top]);

    const onMouseMove = React.useCallback((e: MouseEvent | TouchEvent) => {
        if (isDragging.current) {
            updateDragPosition(e, true, {});
            e.preventDefault();
            e.stopPropagation();
        }
    }, [updateDragPosition]);

    const onMouseUp = React.useCallback((e: MouseEvent | TouchEvent) => {
        currentElement?.removeEventListener('mousemove', onMouseMove, true);
        currentElement?.removeEventListener('mouseup', onMouseUp, true);
        updateDragPosition(e, false, spec, e.target === spec.target.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentElement?.removeEventListener, onMouseMove, updateDragPosition, spec]);

    const onMouseDown = React.useCallback((e: MouseEvent | TouchEvent) => {
        currentElement?.addEventListener('mousemove', onMouseMove, true);
        currentElement?.addEventListener('mouseup', onMouseUp, true);
        updateDragPosition(e, true, spec, e.target === spec.target.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentElement?.addEventListener, onMouseMove, onMouseUp, spec, updateDragPosition]);

    React.useEffect(() => {
        currentElement?.addEventListener('mousedown', onMouseDown, true);

        return () => {
            currentElement?.removeEventListener('mousedown', onMouseDown, true);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentElement?.addEventListener, currentElement?.removeEventListener, onMouseDown, spec.constraintsTo]);

    return [currentDragPosition, setDragPositionCallback, isDragging.current];
};
